import { useAuthCtx } from '@/app/context/auth/authContextController/AuthContextController';
import { useConfirmationStore } from '@/components/dialog/confirmation.store';
import { ConfirmationDialog } from '@/components/dialog/ConfirmationDialog';
import { useDialogFormStore } from '@/components/dialog/form.store';
import { FormDialog as GlobalDialogForm } from '@/components/dialog/FormDialog';
import { LoadingOverlay } from '@/components/loader/Loader';
import { PermissionsProvider } from '@/features/auth/access-control/context/PermissionsProvider';
import { permissionCache } from '@/features/auth/access-control/permissionCache';
import { buildAllowedPermissionSet } from '@/features/auth/access-control/permissionSet';
import { logger } from '@/instrumentation/logger';
import { useApiClientCtx } from '@/react-query/context/ApiClientContextController';
import { TanStackDevtools } from '@tanstack/react-devtools';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useEffect, useMemo } from 'react';
import { Toaster } from 'sonner';

import { ToastErrorIcon } from '@/icons/toast-error-icon';
import { ToastInfoIcon } from '@/icons/toast-info-icon';
import { ToastSuccessIcon } from '@/icons/toast-success-icon';
import { usePermission } from './hooks/useAuth/usePermissions';
import { useRoleId } from './hooks/useAuth/useRoleId';
import { router } from './router';
import { RouterWithPerms } from './router/RouterWithPerms';

const openReactQueryDevtools = import.meta.env.DEV;

if (import.meta.env.VITE_SENTRY_DSN) {
  logger.init();
}

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

export default function App() {
  const confirmationStore = useConfirmationStore();
  const dialogFormStore = useDialogFormStore();
  const { client, queryClient } = useApiClientCtx();
  const auth = useAuthCtx();

  const roleId = useRoleId();

  const permissionEnabled = Boolean(auth.accessToken && roleId);

  const {
    data,
    isFetching: isPermissionsLoading,
    error: permissionsError,
  } = usePermission(roleId ?? '', permissionEnabled);

  /*
   * Store permissions for each role so they are available immediately after a page refresh.
   * The API still runs on every refresh to check for changes. If the permissions changed,
   * update the cache and check the current route again. If there is no cache yet, wait for
   * the API response so an empty permission list does not cause a wrong 403 redirect.
   */
  const cachedPermissionKeys = useMemo(() => (roleId ? permissionCache.read(roleId) : null), [roleId]);
  const apiPermissionKeys = useMemo(
    () => data?.allowedFunctions.map(({ permissionKey }) => permissionKey),
    [data?.allowedFunctions],
  );

  useEffect(() => {
    if (roleId && apiPermissionKeys) {
      const permissionsChanged = permissionCache.writeIfChanged(roleId, apiPermissionKeys);
      if (permissionsChanged) {
        void router.invalidate();
      }
    }
  }, [apiPermissionKeys, roleId]);

  useEffect(() => {
    if (roleId && permissionsError) {
      permissionCache.remove(roleId);
      void router.invalidate();
    }
  }, [permissionsError, roleId]);

  const allowedKeys = useMemo(() => {
    if (!permissionEnabled || permissionsError) return new Set<string>();
    if (data?.allowedFunctions) return buildAllowedPermissionSet(data.allowedFunctions);
    return new Set(cachedPermissionKeys ?? []);
  }, [cachedPermissionKeys, data?.allowedFunctions, permissionEnabled, permissionsError]);

  if (permissionEnabled && isPermissionsLoading) {
    return <LoadingOverlay />;
  }

  return (
    <>
      <PermissionsProvider allowedKeys={allowedKeys}>
        <RouterWithPerms
          confirmationStore={confirmationStore}
          dialogFormStore={dialogFormStore}
          queryClient={queryClient}
          client={client}
          auth={auth}
        />
      </PermissionsProvider>

      <ConfirmationDialog />
      <GlobalDialogForm />
      <Toaster
        toastOptions={{
          unstyled: true,
          classNames: {
            toast:
              'py-2.5 px-6 rounded-sm shadow-toast flex items-center justify-center gap-2 min-w-[339px] text-on-color',
            success: 'bg-success',
            error: 'bg-destructive',
            info: 'bg-warning',
          },
        }}
        icons={{
          success: <ToastSuccessIcon className="size-[25px] text-on-color" />,
          error: <ToastErrorIcon className="size-[25px] text-on-color" />,
          info: <ToastInfoIcon className="size-[25px] text-on-color" />,
        }}
        position="top-center"
      />

      {openReactQueryDevtools && <TanStackDevtools />}
      {openReactQueryDevtools && <ReactQueryDevtools initialIsOpen={false} />}
    </>
  );
}
